import type { InformativeDetailsProps } from './informative-details-types';
import { CaptionAccentTypography, Info, SimpleTooltip } from '@components/atoms';
import { TextTooltip } from '@components/atoms/tooltip/prebuilt/text-tooltip/text-tooltip';
import { type TypographyProps } from '@components/atoms/typography/types/typography.types';
import { cn } from '@utils';

interface InformativeDetailsTitleProps extends TypographyProps {
	tooltipContent?: string | React.ReactNode;
	tooltipAriaLabel?: string;
}

export const InformativeDetailsTitle: React.FC<InformativeDetailsTitleProps> = ({
	className,
	children,
	tooltipContent,
	tooltipAriaLabel = 'informative details title',
}) => {
	return (
		<div className="flex items-center gap-2" data-testid="informative-details-title">
			<CaptionAccentTypography className={cn(className, 'text-primary')}>{children}</CaptionAccentTypography>
			{tooltipContent && (
				<SimpleTooltip content={tooltipContent} ariaLabel={tooltipAriaLabel}>
					<Info className="text-secondary" />
				</SimpleTooltip>
			)}
		</div>
	);
};

InformativeDetailsTitle.displayName = 'InformativeDetailsTitle';

export const InformativeDetails: React.FC<InformativeDetailsProps> = ({
	title,
	titleClassName,
	children,
	testId,
	className,
}) => {
	return (
		<div data-testid={`informative-details-${testId}`} className={className}>
			<InformativeDetailsTitle
				className={titleClassName}
				tooltipContent={title.tooltip}
				tooltipAriaLabel={title.tooltipAriaLabel}
			>
				{title.text}
			</InformativeDetailsTitle>
			{Array.isArray(children) && typeof children[0] === 'string'
				? (children as string[]).map((text, index) => (
						<TextTooltip key={`${text}-${index}`} color="primary" size="xs">
							{text}
						</TextTooltip>
					))
				: children}
		</div>
	);
};

InformativeDetails.displayName = 'InformativeDetails';
